import { Button, CheckIcon, EditIcon, EmptyState, Modal, TextArea } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClassConstraints } from '../../../hooks/useClassConstraints';
import { useConditionsDuplication } from '../../../hooks/useConditionsDuplication';
import { removeNullAndRelatedFields } from '../../../utils/removeNullAndRelatedFields';
import { showSuccessSaveToast } from '../../../utils/toast';
import { ConstraintsItem } from '../../Constraints/ConstraintsItem';
import { DuplicateConditionsModal } from '../../Constraints/DuplicateConditionsModal';
import { ConstraintsForm } from '../../Constraints/Form/ConstraintsForm';
import {
  ConditionsControlProps,
  ConstraintsFormInitialValuesType,
  ConstraintsItemType,
  ConstraintTypes,
  constraintOrder,
} from '../../Constraints/types';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { useVersionConstraints } from '../hooks/useVersionConstraints';

type TimetableVersionClassesConditionsProps = {
  versionUuid: string;
  classUuid: string;
  /** Class name, used for the edit-modal title and duplicate hints. */
  name?: string;
} & ConditionsControlProps;

/**
 * Self-contained conditions tab for a single class on a timetable version: fetches the class↔version
 * constraints blob, renders it as a list (shared {@link ConstraintsItem}) and edits it through the
 * shared {@link ConstraintsForm} modal. Add/edit + duplicate are triggered from the shell action bar
 * (see {@link ConditionsControlProps}); per-item duplicate is triggered from each list row.
 */
export const TimetableVersionClassesConditions = observer(
  ({
    versionUuid,
    classUuid,
    name,
    editOpen,
    setEditOpen,
    duplicateOpen,
    setDuplicateOpen,
    duplicateTargets,
  }: TimetableVersionClassesConditionsProps) => {
    const { t } = useTranslation();
    const store = useTimetableStore();

    const [comment, setComment] = useState('');
    const [duplicateItem, setDuplicateItem] = useState<ConstraintsItemType | null>(null);

    const timeGridEntries = store.timeGridEntries ?? [];

    const { raw, resourceType, write: writeConstraints } = useVersionConstraints(versionUuid, 'class', classUuid);

    const constraintsData = useMemo(() => {
      return JSON.parse(raw ?? JSON.stringify({ comment: '' })) as ConstraintsFormInitialValuesType;
    }, [raw]);

    const constraints = useClassConstraints(constraintsData);

    // Re-sync the editable comment whenever the loaded blob changes (async load, class switch, post-save).
    useEffect(() => {
      setComment(constraintsData.comment ?? '');
    }, [constraintsData.comment]);

    const write = async (targetUuid: string, blob: Record<string, unknown>) => {
      const { error } = await writeConstraints(targetUuid, blob);
      return !error;
    };

    const {
      loading: duplicating,
      duplicateAll,
      duplicateFields,
    } = useConditionsDuplication({
      versionUuid,
      resourceType,
      write,
    });

    async function handleConditionsChange(data: ConstraintsFormInitialValuesType): Promise<boolean> {
      if (store.readonly) return false;
      const cleaned = removeNullAndRelatedFields(data, constraintOrder, 'Priority');
      const { error } = await writeConstraints(classUuid, cleaned as Record<string, unknown>);
      return !error;
    }

    function handleDelete(item: ConstraintsItemType) {
      if (store.readonly) return;
      const type = item.type as ConstraintTypes;
      const priorityName = `${type}Priority`;
      handleConditionsChange({ ...constraintsData, [type]: null, [priorityName]: null });
    }

    function handleCommentChange(e: ChangeEvent<HTMLTextAreaElement>) {
      if (store.readonly) return;
      setComment(e.target.value);
      handleConditionsChange({ ...constraintsData, comment: e.target.value });
    }

    async function handleDuplicateAll(targetUuids: string[]) {
      const cleaned = removeNullAndRelatedFields(constraintsData, constraintOrder, 'Priority');
      await duplicateAll(cleaned, targetUuids);
      setDuplicateOpen(false);
      showSuccessSaveToast();
    }

    async function handleDuplicateItem(targetUuids: string[]) {
      if (!duplicateItem) return;
      const type = duplicateItem.type;
      await duplicateFields(constraintsData, [type, `${type}Priority`], targetUuids);
      setDuplicateItem(null);
      showSuccessSaveToast();
    }

    return (
      <>
        {constraints.length === 0 ? (
          <EmptyState
            icon={<CheckIcon className='bordered' />}
            size='small'
            iconColor='var(--color-success)'
            title={t('timetableVersion.constraint.noConstraints')}
            padding='xl'
          />
        ) : (
          <>
            {constraintsData.comment && (
              <TextArea
                className='px-6'
                name='comment'
                onChange={handleCommentChange}
                label={t('common.comment')}
                value={comment}
                isScrollable
                readonly={store.readonly}
              />
            )}
            {constraints.map((constraint) => (
              <ConstraintsItem
                key={constraint.type}
                item={constraint}
                onDelete={handleDelete}
                onDuplicate={(item) => setDuplicateItem(item)}
              />
            ))}
          </>
        )}

        <div className='tks__row px-6 py-4' style={{ justifyContent: 'center' }}>
          <Button hierarchy='secondary' icon={<EditIcon />} onClick={() => setEditOpen(true)} disabled={store.readonly}>
            {t('constraint.edit')}
          </Button>
        </div>

        <Modal
          isOpen={editOpen}
          onRequestClose={() => setEditOpen(false)}
          shouldCloseOnEsc={false}
          shouldCloseOnOverlayClick={false}
          title={t('conditions.for', { for: name ?? '' })}
        >
          <ConstraintsForm
            constraints={constraintsData}
            timeGridEntries={timeGridEntries}
            onSubmit={(values) => handleConditionsChange(values)}
            onClose={() => setEditOpen(false)}
          />
        </Modal>

        <DuplicateConditionsModal
          isOpen={duplicateOpen}
          onClose={() => setDuplicateOpen(false)}
          title={t('conditions.duplicateModal.title')}
          hint={t('conditions.duplicateModal.hint', { name: name ?? '' })}
          options={duplicateTargets}
          isLoading={duplicating}
          onConfirm={handleDuplicateAll}
        />

        <DuplicateConditionsModal
          isOpen={!!duplicateItem}
          onClose={() => setDuplicateItem(null)}
          title={t('conditions.duplicateModal.titleSingle')}
          hint={t('conditions.duplicateModal.hintSingle', { name: name ?? '' })}
          options={duplicateTargets}
          isLoading={duplicating}
          onConfirm={handleDuplicateItem}
        />
      </>
    );
  },
);
